import React from 'react';
import PropTypes from 'prop-types';
import { FaFilePowerpoint } from 'react-icons/fa';
import styles from './ExecSummaryLink.module.css';
import { trackPastIncidentsExecSummaryClick } from '../../../../utils/modernUITracking';

// Only https links are rendered, so a corrupted stored value can never become a javascript:
// or data: href. The server validates on save; this is the second line.
const isHttps = (url) => {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * ExecSummaryLink - the Past Incidents icon that opens an incident's Exec Summary Slide
 * (US2135577). Renders nothing when there is no usable link, or a same-width spacer when
 * `reserveSpace` is set so a list's other items do not shift between rows with and without one.
 *
 * Its host (a list row or a card) toggles on click AND on Enter/Space keydown, calling
 * preventDefault on the latter, so both are stopped here: a keydown left to bubble would be
 * cancelled and Enter on the focused link would never navigate.
 */
const ExecSummaryLink = ({ url, incidentId, source, reserveSpace = false, newrelic = null }) => {
  if (!url || !isHttps(url)) {
    return reserveSpace ? <span className={styles.spacer} aria-hidden="true" /> : null;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.link}
      aria-label="Exec Summary"
      title="Exec Summary"
      data-testid="exec-summary-link"
      onClick={(e) => {
        e.stopPropagation();
        trackPastIncidentsExecSummaryClick({ incidentId, source }, newrelic);
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <FaFilePowerpoint aria-hidden="true" />
    </a>
  );
};

ExecSummaryLink.propTypes = {
  url: PropTypes.string,
  incidentId: PropTypes.string,
  source: PropTypes.oneOf(['list_row', 'card']).isRequired,
  reserveSpace: PropTypes.bool,
  newrelic: PropTypes.object,
};

export default ExecSummaryLink;
